import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import { MapPin, Truck, AlertCircle } from "lucide-react";

// Lazy imports — only loaded once token is available
let Map = null;
let Marker = null;
let mapboxImportPromise = null;

async function loadMapbox() {
  if (!mapboxImportPromise) {
    mapboxImportPromise = (async () => {
      const mod = await import("react-map-gl/mapbox");
      await import("mapbox-gl/dist/mapbox-gl.css");
      Map = mod.default || mod.Map;
      Marker = mod.Marker;
      return true;
    })();
  }
  return mapboxImportPromise;
}

const CH_CENTER = { longitude: 8.2275, latitude: 46.8182, zoom: 7.5 };

export default function MapView() {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState({ drivers: [], orders: [] });
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data: cfg } = await api.get("/map/config");
        setConfig(cfg);
        if (cfg.mapbox_token) {
          await loadMapbox();
          setReady(true);
        }
      } catch (e) { setError("Karten-Konfiguration konnte nicht geladen werden."); }
    })();
  }, []);

  useEffect(() => {
    if (!config?.mapbox_token) return;
    let cancelled = false;
    const fetchData = async () => {
      try {
        const { data: d } = await api.get("/map/data");
        if (!cancelled) setData(d);
      } catch {}
    };
    fetchData();
    const id = setInterval(fetchData, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [config]);

  const noToken = config && !config.mapbox_token;

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Live-Übersicht</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Karte</h1>
            <p className="text-sm text-[var(--text-3)] mt-1">Offene Aufträge und Fahrerpositionen in Echtzeit (Update alle 30 s).</p>
          </div>
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#D4AF37" }} /> Auftrag</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: "#22D3EE" }} /> Fahrer</span>
          </div>
        </div>

        {error && <div className="mt-6 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        {noToken && (
          <div className="mt-8 card p-10 text-center" data-testid="map-no-token">
            <AlertCircle size={32} strokeWidth={1.5} className="mx-auto text-[#D4AF37]" />
            <h2 className="text-xl font-semibold mt-4">Mapbox-Token fehlt</h2>
            <p className="text-sm text-[var(--text-2)] mt-2 max-w-md mx-auto">
              Hinterlegen Sie den Mapbox Public Token in <code className="text-[#D4AF37] font-mono">backend/.env</code> als <code className="text-[#D4AF37] font-mono">MAPBOX_PUBLIC_TOKEN</code> und starten Sie den Backend neu — danach lädt diese Seite automatisch die Karte.
            </p>
            <a href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:text-[var(--text)] mt-4 inline-block uppercase tracking-wider">Token bei Mapbox abrufen →</a>
          </div>
        )}

        {ready && config?.mapbox_token && (
          <div className="card mt-6 overflow-hidden" style={{ height: "70vh" }}>
            <Map
              mapboxAccessToken={config.mapbox_token}
              mapStyle="mapbox://styles/mapbox/dark-v11"
              initialViewState={CH_CENTER}
              style={{ width: "100%", height: "100%" }}
            >
              {data.orders.map((o) => (
                o.delivery?.lat && o.delivery?.lng ? (
                  <Marker key={`o-${o.id}`} longitude={o.delivery.lng} latitude={o.delivery.lat} anchor="bottom">
                    <button
                      onClick={() => navigate(`/admin/orders/${o.id}`)}
                      title={`${o.order_number} · ${o.delivery?.city || ""}`}
                      className="block"
                    >
                      <div className="w-3 h-3 rounded-full border-2 border-black" style={{ background: "#D4AF37" }} />
                    </button>
                  </Marker>
                ) : null
              ))}
              {data.drivers.map((d) => (
                d.last_position?.lat ? (
                  <Marker key={`d-${d.id}`} longitude={d.last_position.lng} latitude={d.last_position.lat} anchor="center">
                    <div className="relative">
                      <div className="w-4 h-4 rounded-full border-2 border-black animate-pulse" style={{ background: "#22D3EE" }} />
                    </div>
                  </Marker>
                ) : null
              ))}
            </Map>
          </div>
        )}

        {/* Side stats */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="card p-5">
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Offene Aufträge</div>
            <div className="text-3xl font-mono mt-2">{data.orders.length}</div>
          </div>
          <div className="card p-5">
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Aktive Fahrer</div>
            <div className="text-3xl font-mono mt-2">{data.drivers.length}</div>
          </div>
          <div className="card p-5">
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Status</div>
            <div className="text-sm mt-2">{noToken ? "⚠ Mapbox nicht konfiguriert" : ready ? "✓ Live" : "Lade…"}</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
